import { FC, ReactNode } from 'react'
import { useRevalidator } from 'react-router-dom'
import PageHeader from './PageHeader'
import PageLoadingSupport from '../PageLoadingSupport'
import FullPageLoader from '../FullPageLoader'
import { Stack } from '@mui/material'
import useAppStore from '$appStore'

type PageLayoutProps = {
  children: ReactNode
  title?: string
  displayLeftTitle?: boolean
  displayDateFilter?: boolean
  customBottomLeftComponent?: string
  /**
   * If true, only the HTML title will be set, not the in-page title.
   * Note this also prevents the <PageHeader /> from being rendered.
   */
  htmlTitleOnly?: boolean
}

/**
 * Main page layout component.
 * All pages should be presented via this component - i.e. a route should
 * render one of these components with a child component for whatever should
 * be shown in the main content area.
 *
 * Handle dynamic page titles via the usePageTitle hook in the page component.
 */
const PageLayout: FC<PageLayoutProps> = ({
  children,
  title,
  displayLeftTitle,
  displayDateFilter,
  customBottomLeftComponent,
  htmlTitleOnly = false,
}) => {
  const revalidator = useRevalidator()

  const pageTitleFromStore = useAppStore((state) => state.pageTitle)

  // set the effective title, this should be the title, or if the pageTitleFromStore is not null, that takes precedence
  // If both are null, then the effective title is undefined
  const effectiveTitle =
    title ?? (pageTitleFromStore ? pageTitleFromStore : undefined)

  return (
    // Spacing property doesn't work in stack because of the render order
    // so we need to use the sx property
    <Stack
      sx={{
        marginBottom:
          revalidator.state === 'loading'
            ? '0rem'
            : effectiveTitle
            ? '1.6rem'
            : '0rem',
      }}
    >
      {effectiveTitle && !htmlTitleOnly && (
        <PageHeader
          title={effectiveTitle}
          displayLeftTitle={displayLeftTitle}
          displayDateFilter={displayDateFilter}
          customBottomLeftComponent={customBottomLeftComponent}
        />
      )}
      <PageLoadingSupport title={effectiveTitle ?? undefined}>
        {revalidator.state === 'loading' ? <FullPageLoader /> : children}
      </PageLoadingSupport>
    </Stack>
  )
}

export default PageLayout
